// orderSort.js — Sunjin canonical line-item sort order (PORTAL copy).
//
// Every place an order is OUTPUT (QB invoice, packing sheet, admin order
// detail) must show line items in this fixed order, regardless of the order
// the customer added them to the cart:
//
//   1. Tofu SKUs first, in the exact TOFU_ORDER sequence below.
//   2. Then Refrigerated (냉장) products, by SKU ascending.
//   3. Then Frozen (냉동) products, by SKU ascending.
//   4. Then Dry products, by SKU ascending.
//   5. Products with no / unrecognized storage method go LAST, by SKU ascending,
//      and are reported back so the caller can warn about them.
//
// NOTE: storageMethod is stored on product records as the English strings
// "Refrigerated" / "Frozen" / "Dry" (see AdminProducts.jsx STORAGE_METHODS).
//
// ⚠ There is a SECOND copy of this rule in the sync-server repo at
//    C:\SunjinQBSync\orderSort.js — they must be kept in sync by hand.
//    If you edit TOFU_ORDER or STORAGE_RANK here, edit it there too.

// Edit this list to change the tofu ordering later.
export const TOFU_ORDER = [
  "10001", "10002", "10007", "10008", "10061", "10069", "10078", "10098", "40891",
];

// Group ranks for the non-tofu buckets (2 = refrigerated, 3 = frozen, 4 = dry).
export const STORAGE_RANK = {
  Refrigerated: 2,
  Frozen: 3,
  Dry: 4,
};

const TOFU_INDEX = new Map(TOFU_ORDER.map((sku, i) => [sku, i]));
const NO_STORAGE_RANK = 5; // group 5: unset / unrecognized storage -> last

function skuNum(sku) {
  const n = parseInt(sku, 10);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

const CREDIT_RANK = 6; // credit lines (item 10000) always sort dead last

// Returns { group, tofuIndex } describing where an item sorts.
function classify(item, storageBySku) {
  if (item.creditLine) return { group: CREDIT_RANK, tofuIndex: -1 };
  const key = String(item.sku);
  if (TOFU_INDEX.has(key)) {
    return { group: 1, tofuIndex: TOFU_INDEX.get(key) };
  }
  const method = storageBySku[key];
  const group = STORAGE_RANK[method] || NO_STORAGE_RANK;
  return { group, tofuIndex: -1 };
}

/**
 * Sort an order's line items into the canonical Sunjin output order.
 *
 * @param {Array<{sku: string}>} items  Order line items (each must have .sku).
 * @param {Object<string,string>} storageBySku  Map of sku -> storageMethod
 *        (e.g. { "10005": "Refrigerated" }). Missing keys are treated as unset.
 * @returns {{ sorted: Array, missingStorageSkus: string[] }}
 *        sorted = items in canonical order (a new array; input is not mutated);
 *        missingStorageSkus = SKUs that fell into group 5 (no/unknown storage),
 *        so the caller can warn about product records that need fixing.
 */
export function sortOrderItems(items, storageBySku = {}) {
  const list = Array.isArray(items) ? items : [];
  const decorated = list.map((item, originalIndex) => ({
    item,
    originalIndex,
    ...classify(item, storageBySku),
  }));

  decorated.sort((a, b) => {
    if (a.group !== b.group) return a.group - b.group;
    if (a.group === 1) return a.tofuIndex - b.tofuIndex; // tofu: fixed sequence
    const na = skuNum(a.item.sku);
    const nb = skuNum(b.item.sku);
    if (na !== nb) return na - nb;                        // groups 2-5: SKU asc
    // Stable tiebreakers so equal keys keep a deterministic order.
    const sa = String(a.item.sku);
    const sb = String(b.item.sku);
    if (sa !== sb) return sa < sb ? -1 : 1;
    return a.originalIndex - b.originalIndex;
  });

  const missingStorageSkus = decorated
    .filter(d => d.group === NO_STORAGE_RANK)
    .map(d => String(d.item.sku));

  return { sorted: decorated.map(d => d.item), missingStorageSkus };
}
